import { SagaIterator } from 'redux-saga';
import { select } from 'redux-saga/effects';
import { App, isBeibehalten, isTiergruppeActive } from '@ps20-frontend/frontend-models';
import {
  AngebotsId,
  GlobalAction,
  WrappedSavedStates,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { IAppState } from '../appState';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import {
  APPS_REPLACED_BY_TGV,
  selectAvailableProduktauswahlItemsForDashboard,
} from '../selectors/produktauswahl.selector';

interface LoadedTgvState {
  tiergruppe?: {
    angebotsIds?: Record<string, string>;
    thhAngelegt?: { thhHund?: boolean; thhPferd?: boolean };
    deferredDeletion?: string[];
  };
}

const isOwnedByTgv = (angebot: WarenkorbAngebot): boolean => angebot.ownerAppId === App.TGV;

const listedByLoadedTgv = (
  savedStates: WrappedSavedStates,
): ((angebot: WarenkorbAngebot) => boolean) => {
  const loadedTgv = savedStates.find(loadedState => loadedState.appId === App.TGV)?.state as
    | LoadedTgvState
    | undefined;
  const listedTkvIds = new Set(Object.values(loadedTgv?.tiergruppe?.angebotsIds ?? {}));
  const thhAngelegt = loadedTgv?.tiergruppe?.thhAngelegt;
  const deferredKeys = new Set(loadedTgv?.tiergruppe?.deferredDeletion ?? []);
  return angebot =>
    (angebot.appId === App.TKV_V2 &&
      listedTkvIds.has(angebot.angebotsId) &&
      !deferredKeys.has(angebot.angebotsId)) ||
    (angebot.appId === App.THH_HUND &&
      thhAngelegt?.thhHund === true &&
      !deferredKeys.has(App.THH_HUND)) ||
    (angebot.appId === App.THH_PFERD &&
      thhAngelegt?.thhPferd === true &&
      !deferredKeys.has(App.THH_PFERD));
};

const hasLoadedTkvWrapper = (
  savedStates: WrappedSavedStates,
): ((angebot: WarenkorbAngebot) => boolean) => {
  const loadedTkvIds = new Set(
    savedStates
      .filter(loadedState => loadedState.appId === App.TKV_V2)
      .map(loadedState => loadedState.angebotsId ?? loadedState.state?.angebotsId),
  );
  return angebot => angebot.appId !== App.TKV_V2 || loadedTkvIds.has(angebot.angebotsId);
};

export function* collectOrphanedTiergruppeProducts(
  action: GlobalAction<WrappedSavedStatesLoaded>,
): SagaIterator<AngebotsId[]> {
  const state: IAppState = yield select();
  const warenkorb = state.warenkorb ?? [];
  const savedStates = action.payload.savedStatesLoaded;
  const candidates = new Set<WarenkorbAngebot>();

  const isTgvTileAvailable = selectAvailableProduktauswahlItemsForDashboard(state).some(
    item => item.appId === App.TGV,
  );
  if (isTgvTileAvailable && !state.juristisch) {
    warenkorb
      .filter(
        angebot =>
          !angebot.ownerAppId &&
          APPS_REPLACED_BY_TGV.has(angebot.appId) &&
          !isBeibehalten(angebot) &&
          (angebot.vorvertragsNummern?.length ?? 0) === 0,
      )
      .forEach(angebot => candidates.add(angebot));
  }

  if (isTiergruppeActive(state.transientState?.featureToggles)) {
    const hasTgvRow = warenkorb.some(row => row.appId === App.TGV);
    const isListed = hasTgvRow ? listedByLoadedTgv(savedStates) : () => false;
    const hasWrapper = hasLoadedTkvWrapper(savedStates);
    warenkorb
      .filter(isOwnedByTgv)
      .filter(angebot => !isListed(angebot) || !hasWrapper(angebot))
      .forEach(angebot => candidates.add(angebot));
  }

  return Array.from(candidates).map(({ appId, angebotsId }) => ({ appId, angebotsId }));
}
